import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { NEW_CARD_OPTION } from './CreditCardSelectField';

const paymentFormSchema = (intl) =>
  z.object({
    cardholderName: z
      .string()
      .min(2, { message: intl.formatMessage({
          id: 'payment_form.enter_cardholder_name_error',
          defaultMessage: 'Enter cardholder\'s name and last name'
        })
      })
      .optional()
      .or(z.literal('')),
    selectedCreditCard: z.object({
      label: z.string().optional(),
      id: z.string().optional(),
      value: z.string().optional()
    }).optional(),
    selectedPaymentMethod: z.string().optional()
  }).superRefine((values, ctx) => {
    const isCardPayment = values?.selectedPaymentMethod === 'card';
    const isNewCard = values?.selectedCreditCard?.id === NEW_CARD_OPTION.id;
    if (isNewCard && isCardPayment) {
      if (!values?.cardholderName || !values?.cardholderName?.length >= 2) {
        ctx.addIssue({
          path: ["cardholderName"],
          message: intl.formatMessage({
            id: 'payment_form.enter_cardholder_name_error',
            defaultMessage: 'Enter cardholder\'s name and last name'
          }),
          code: "custom",
        });
      }
    }
  });

export const usePaymentForm = ({
  onChange,
  defaultValues,
  debounceDelay = 1000,
  intl
}) => {
  const paymentForm = useForm({
    defaultValues: {
      selectedCreditCard: '',                 // Selected existing card
      selectedPaymentMethod: 'card',          // Google Pay / Apple Pay / Credit Card
      selectedPaymentOption: 'pay_full',      // Split / Full payment
      totalCreditsApplied: 0,                 // PS credits
      cardholderName: '',
      ...(defaultValues || {})
    },
    resolver: zodResolver(paymentFormSchema(intl))
  });

  const prevFormValues = React.useRef(cloneDeep(paymentForm.getValues()));
  const getChangedFields = () => {
    const newValues = paymentForm.getValues();
    const changedFields = Object.keys(newValues).filter(
      (field) => !isEqual(newValues[field], prevFormValues.current[field])
    );

    return changedFields;
  };

  const debouncedOnChange = debounce((newValues) => {
    onChange?.(newValues, getChangedFields(newValues));
    prevFormValues.current = cloneDeep(newValues);
  }, debounceDelay);

  const _selectedPaymentMethod = paymentForm.watch('selectedPaymentMethod');
  const _selectedCreditCard = paymentForm.watch('selectedCreditCard');

  // Trigger cardholderName validation when card and method changes
  React.useEffect(() => {
    paymentForm.clearErrors('cardholderName');
    if (paymentForm?.formState?.isSubmitted) {
      paymentForm.trigger('cardholderName');
    }
  }, [_selectedPaymentMethod, _selectedCreditCard, paymentForm?.formState?.isSubmitted]);

  React.useEffect(() => {
    const formSubscription = paymentForm.watch((newValues, updatedField) => {
      if (updatedField?.name === 'totalCreditsApplied') {
        // skip debounce for apply credits button click
        onChange?.(newValues, ['totalCreditsApplied']);
      } else {
        debouncedOnChange(newValues);
      }
    });
    return () => {
      formSubscription?.unsubscribe();
    };
  }, []);

  return { paymentForm };
};
